/**
 * Soft fill-capacity guidance — customer language, not engine jargon.
 * Uses catalog fill_capacity_percent / capacity_tier from the API.
 */

const FILLABLE_EPS = 1e-6;

export const CAPACITY_TIP_STORAGE_KEY = 'ojasya_capacity_marks_tip_seen';

export const CAPACITY_PANEL_TIP =
  'Marks show how much of a cup each botanical can fill';

export function readCapacityTipSeen() {
  try {
    return window.sessionStorage.getItem(CAPACITY_TIP_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeCapacityTipSeen() {
  try {
    window.sessionStorage.setItem(CAPACITY_TIP_STORAGE_KEY, '1');
  } catch {
    // ignore private-mode / blocked storage
  }
}

/**
 * @param {string[]} selectedIds
 * @param {Array<{ id: string, fill_capacity_percent?: number, capacity_tier?: string }>} ingredients
 * @returns {string | null}
 */
export function getSelectionCapacityGuidance(selectedIds, ingredients) {
  if (!selectedIds || selectedIds.length === 0) return null;

  const items = selectedIds
    .map((id) => ingredients.find((ing) => ing.id === id))
    .filter(Boolean);

  if (items.length === 0) return null;

  const fillSum = items.reduce(
    (sum, ing) => sum + Number(ing.fill_capacity_percent || 0),
    0
  );

  if (fillSum + FILLABLE_EPS < 100) {
    return 'Add one fuller mark so these accents have a home.';
  }

  const lowCount = items.filter((ing) => ing.capacity_tier === 'low').length;
  const hasBody = items.some(
    (ing) => ing.capacity_tier === 'full' || ing.capacity_tier === 'high'
  );
  if (!hasBody && lowCount >= 2 && items.length >= 3) {
    return 'Lots of accents — add one fuller botanical for body.';
  }

  return null;
}

/** @param {'full' | 'high' | 'mid' | 'low' | string | undefined} tier */
export function capacityTickCount(tier) {
  if (tier === 'full') return 4;
  if (tier === 'high') return 3;
  if (tier === 'mid') return 2;
  return 1;
}

/** Short phrase for aria / popover title. */
export function capacityTierLabel(tier) {
  if (tier === 'full') return 'Cup body';
  if (tier === 'high') return 'Strong body';
  if (tier === 'mid') return 'Partner';
  return 'Accent';
}

/** One plain sentence for popover / detail modal. */
export function capacityTierDescription(tier) {
  if (tier === 'full') return 'Fills most of the cup.';
  if (tier === 'high') return 'Fills much of the cup — usually needs company.';
  if (tier === 'mid') return 'Shares the cup with fuller botanicals.';
  return 'A little goes a long way.';
}
